import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import HaloRing from "@/components/HaloRing";
import HaloWordmark from "@/components/HaloWordmark";
import { ArrowLeft, ShieldCheck } from "lucide-react";

export default function ResetPassword() {
  const { resetPassword } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError("Those two passwords don't match"); return; }
    setBusy(true);
    setError("");
    try {
      await resetPassword(token, password);
      toast.success("Password updated — you're signed in");
      nav("/");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not reset your password");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-background px-6" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center gap-3">
          <HaloRing size={30} />
          <HaloWordmark height={19} data-testid="login-wordmark" />
        </div>

        {!token ? (
          <div className="studio-panel p-6" data-testid="reset-missing-token">
            <h1 className="text-2xl font-semibold tracking-tight">This link looks incomplete</h1>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              Open the reset link from your email again, or request a fresh one.
            </p>
            <Link to="/forgot-password" className="mt-6 inline-block" data-testid="reset-request-new">
              <Button variant="outline" className="rounded-full">Request a new link</Button>
            </Link>
          </div>
        ) : (
          <>
            <ShieldCheck className="h-6 w-6 text-[hsl(var(--halo-teal))]" />
            <h1 className="mt-4 text-3xl font-semibold tracking-tight">Set a new password</h1>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              Choose something at least 8 characters long. Saving it signs out every other device.
            </p>
            <form onSubmit={submit} className="mt-7 space-y-4" data-testid="reset-form">
              <div>
                <Label htmlFor="rp-pass">New password</Label>
                <Input id="rp-pass" data-testid="reset-password" type="password" minLength={8} required
                       value={password} onChange={(e) => setPassword(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="rp-confirm">Confirm password</Label>
                <Input id="rp-confirm" data-testid="reset-password-confirm" type="password" minLength={8} required
                       value={confirm} onChange={(e) => setConfirm(e.target.value)} />
              </div>
              {error && (
                <p className="text-sm text-[hsl(var(--studio-danger))]" data-testid="reset-error">{error}</p>
              )}
              <Button type="submit" disabled={busy} data-testid="reset-submit"
                      className="h-11 w-full bg-[hsl(var(--studio-primary))] hover:bg-[hsl(var(--studio-primary-hover))]">
                {busy ? "Saving…" : "Save new password"}
              </Button>
            </form>
            <Link to="/login" className="mt-6 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
                  data-testid="reset-back-to-login">
              <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
